import React, { useEffect, useState } from "react";
import { http, useAuth } from "@/context/AuthContext";
import { Users, UserPlus, Calendar, Receipt, Megaphone, Mail, Loader2, MessageSquareQuote, Star, Plug } from "lucide-react";
import { Link } from "react-router-dom";
import PaymentHealth from "@/components/PaymentHealth";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy/55">{label}</div>
          <div className="font-serif text-3xl text-navy mt-1">{value ?? 0}</div>
        </div>
        <Icon className="w-7 h-7 text-gold" />
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      http.get("/admin/stats").then((r) => setStats(r.data)),
      http.get("/admin/reviews").then((r) => setReviews(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-navy/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <header>
        <div className="eyebrow">Overview</div>
        <h1 className="mt-2 text-4xl">Hello, {user?.name?.split(" ")[0]}.</h1>
        <p className="mt-2 text-navy/65">A snapshot of the ConnectHer ecosystem.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Active Clients" value={stats?.clients} />
        <Stat icon={UserPlus} label="New Leads" value={stats?.leads_new} />
        <Stat icon={Calendar} label="Appointments" value={stats?.appointments_total} />
        <Stat icon={Receipt} label="Open Invoices" value={stats?.invoices_open} />
        <Stat icon={Mail} label="Newsletter Subs" value={stats?.newsletter_subscribers} />
        <Stat icon={Users} label="Team Members" value={stats?.team_members} />
        <Stat icon={UserPlus} label="Total Leads" value={stats?.leads_total} />
      </div>

      <PaymentHealth />

      <div className="grid md:grid-cols-3 gap-4">
        <Link to="/admin/leads" className="card p-6 hover:-translate-y-1 transition" data-testid="quick-leads">
          <UserPlus className="w-7 h-7 text-gold mb-3" />
          <h3 className="text-xl">Triage Leads</h3>
          <p className="mt-1 text-sm text-navy/65">Review new inquiries and move them through your pipeline.</p>
        </Link>
        <Link to="/admin/clients" className="card p-6 hover:-translate-y-1 transition" data-testid="quick-clients">
          <Users className="w-7 h-7 text-teal mb-3" />
          <h3 className="text-xl">Manage Clients</h3>
          <p className="mt-1 text-sm text-navy/65">See every client's services, appointments, invoices and docs.</p>
        </Link>
        <Link to="/admin/announcements" className="card p-6 hover:-translate-y-1 transition" data-testid="quick-announcements">
          <Megaphone className="w-7 h-7 text-navy mb-3" />
          <h3 className="text-xl">Send Announcement</h3>
          <p className="mt-1 text-sm text-navy/65">Broadcast to every client in their portal inbox.</p>
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-6 md:col-span-2" data-testid="overview-testimonials">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageSquareQuote className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-xl text-navy">Latest Testimonials</h3>
            </div>
            <Link to="/admin/reviews" className="text-xs text-gold uppercase tracking-widest hover:underline" data-testid="overview-testimonials-manage">
              Manage →
            </Link>
          </div>
          {reviews.length === 0 ? (
            <p className="text-sm text-navy/55">
              No testimonials yet. <Link to="/admin/reviews" className="text-gold hover:underline">Add your first</Link> — they boost SEO and conversion.
            </p>
          ) : (
            <ul className="space-y-3">
              {reviews.slice(0, 3).map((r) => (
                <li key={r.review_id} className="text-sm border-l-2 border-gold/40 pl-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-navy">{r.author}</span>
                    <span className="flex gap-0.5">
                      {Array.from({ length: r.rating || 5 }).map((_, i) => (
                        <Star key={i} className="w-3 h-3 text-gold fill-current" />
                      ))}
                    </span>
                  </div>
                  <p className="text-navy/70 italic line-clamp-2">&ldquo;{r.body}&rdquo;</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Link to="/admin/integrations" className="card p-6 hover:-translate-y-1 transition" data-testid="quick-integrations">
          <Plug className="w-7 h-7 text-teal mb-3" />
          <h3 className="text-xl">Integration Health</h3>
          <p className="mt-1 text-sm text-navy/65">See what&apos;s connected: Stripe, Bookipi, CDM, Mailchimp, HubSpot.</p>
        </Link>
      </div>
    </div>
  );
}
